<template>
<v-app>
  <prb-toolbar v-bind:disable="!terms"></prb-toolbar>
  <prb-terms v-bind:termsDialog="!terms"></prb-terms>
  <main>
    <v-container fluid>
      <v-layout class="limitations__layout">
        <v-flex xs12>
          <div class="limitations__layout-card">
            <v-card>
              <v-card-title class="pa-0">
                <div class="top__layout-title">Limitations</div>
              </v-card-title>
              <v-card-text>
                <p class="body-1">
                  Probe Miner uses data obtained from the canSAR knowledgebase <a :href="apiUrl" target="_blank">({{apiUrl}})</a> – that integrates the public medicinal chemistry databases ChEMBL and BindingDB – and an algorithm to objectively assess all the available ligands for a given target and prioritise them as potential chemical probes based on six chemical probe scores, namely: Target Potency Score, Selectivity Score, Cell Potency Score, SAR Score, Inactive Analogs Score and PAINS Score. These six scores are integrated in a Global Score using different weights that can be customised by the user. It is important to stress that Probe Miner is not exempt from limitations, particularly due to the underlying medicinal chemistry data that it uses. It is very important to understand the limitations of Probe Miner before using it. The main limitations are:
                </p>
                <br/>
                <p><span class="title">1. Data incompleteness</span></p>
                <p class="body-1">
                  Public medicinal chemistry databases do not cover all the available literature. Therefore, although public medicinal chemistry databases cover a large proportion of bioactive small molecules, there is always the risk that a chemical probe is published in a journal that is not covered by public databases and, accordingly, the probe is missing in Probe Miner. Moreover, it is also possible that the probe is present in Probe Miner but key information is missing from public medicinal chemistry databases, thus affecting the ranking of the probe. To mitigate this issue, we have an ongoing data curation effort in collaboration with our sister knowledgebase canSAR to increase the coverage of key chemical biology literature. However, we strongly recommend users to consult the literature very carefully and to use Probe Miner alongside expert curation approaches such as The Chemical Probes Portal whenever possible.
                </p>
                <br/>
                <p><span class="title">2. Data inaccuracies</span></p>
                <p class="body-1">
                  To reduce the risk of inaccuracies we use only public medicinal chemistry databases that are highly curated. However, no database is exempt from errors or inaccuracies in data entries. Therefore, it is possible that errors or inaccuracies in the data may affect the ranking of a chemical probe. To correct such errors and inaccuracies, we have an extensive ongoing curation effort in collaboration with our sister knowledgebase, canSAR. If you are aware of key missing information or find an error, please let us know on <a href="mailto:chemprobes@icr.ac.uk">chemprobes@icr.ac.uk</a>.
                </p>
                <br/>
                <p><span class="title">3. Ranking</span></p>
                <p class="body-1">
                  In some cases, the first ranked compound in Probe Miner may not necessarily be the best available chemical probe for the target of interest. This often occurs when data incompleteness or inaccuracies occur or when there is a very wide variation in the characterisation of chemical probes for the target of interest. In most of those cases, the top ranking compound can be flagged when it is not selective for the target of interest. We have added a danger icon by the chemical structure in the target synopsis page to help users identify those cases. Accordingly, it is not recommended to select the top ranking compounds in Probe Miner without carefully evaluating all the data available and we strongly recommend to also consult expert-curation resources such as The Chemical Probes Portal.
                </p>
                <br/>
                <p><span class="title">4. PAINS</span></p>
                <p class="body-1">
                  Pan-assay interference compounds (PAINS) are those that interfere with the detection methods of screening assays and are problematic compounds that have been identified to be widely used in many scientific publications as chemical tools, thus leading to incorrect conclusions (Baell and Walters, 2014). There are several computational methods that can be used to predict PAINS and the presence of other potentially problematic functional groups in biochemical and cell-based chemical tools (Huggins, et al. 2011). However, none of the available computational methods is exempt from limitations, among them a considerable number of false-positives that are not actually PAINS (Huggins, et al. 2011; Capuzzi et al., 2017). Accordingly, the PAINS Score measures whether a compound is predicted to be PAINS-free using the substructure filters proposed by Baell et al. (see Methods) as a pragmatic means to alert users to the potential risk of functionality in the molecule that may cause misleading effects in biochemical or cell-based assays. However, to reduce the impact of false-positives we give this PAINS Score a low weighting in the default Global Score so as to reduce the risk of deprioritising otherwise promising molecules. It is important to highlight that the PAINS Score is a prediction calculated using only one computational method. Thus, we enable researchers to set the weight of the PAINS Score within the overall Global Score altogether according to their expert judgement and the specific assay that they may be using. Moreover, researchers can also remove the PAINS Score from the calculation of the Global Score altogether by setting the weight to 0.
                </p>
              </v-card-text>
              <v-divider></v-divider>
            </v-card>
          </div>
        </v-flex>
      </v-layout>
    </v-container>
  </main>
  <prb-footer></prb-footer>
</v-app>
</template>

<script>
import {
  mapGetters
} from 'vuex'

import PrbToolbar from './prb-toolbar/PrbToolbar';
import PrbTerms from './prb-terms/PrbTerms';
import PrbFooter from './prb-footer/PrbFooter';

export default {
  components: {
    PrbToolbar,
    PrbTerms,
    PrbFooter
  },
  computed: mapGetters([
    'termsVerified'
  ]),
  metaInfo: {
    title: 'Probe Miner',
    titleTemplate: '%s | Limitations'
  },
  name: 'prblimitations',
  computed: {
    terms: {
      get() {
        return this.$store.state.termsVerified
      }
    }
  },
  data() {
    return {
      apiUrl: process.env.API_URL,
      termsDialog: true,
      search: null,
      e1: 1
    }
  }
}
</script>

<style scoped>
.limitations__layout {
  height: 100%;
  background: url(../img/layer6.png) no-repeat center center fixed;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}

.limitations__layout-card {
  padding: 5% 5% 0 5%;
}

.top__layout-title {
  width: 100%;
  font-size: 30px;
  padding: 10px;
  font-weight: 300;
  background-color: #455A64;
  color: white;
  margin: 0 0 5px 0;
}

@media screen and (max-width: 1600px) {
  .limitations__layout-card {
    padding: 2% 2% 0 2%;
  }
}

@media screen and (max-width: 800px) {
  .limitations__layout-card {
    padding: 2% 0 0 0;
  }
}
</style>



// WEBPACK FOOTER //
// PrbLimitations.vue?7af6eaaa